import { API_BASE_URL } from './utils';
import { enqueueAction, type QueuedActionType } from './offlineQueue';
import { ACTION_QUEUE_CHANGED_EVENT } from './actionQueueProcessor';
import { processActionQueue } from './actionQueueProcessor';

export interface SubmitOrQueueOptions {
  method: string;
  path: string;
  body?: unknown;
  token: string | null;
  /** Use a typed queue entry instead of generic api_request when replay needs special handling */
  queueType?: QueuedActionType;
  queuePayload?: Record<string, unknown>;
}

export interface SubmitOrQueueResult {
  ok: boolean;
  queued: boolean;
  status?: number;
}

function isRetryable(status: number | undefined, error: unknown): boolean {
  if (!navigator.onLine) return true;
  if (error) return true;
  if (status == null) return true;
  if (status >= 500) return true;
  if (status === 408 || status === 429) return true;
  return false;
}

export async function submitOrQueue(options: SubmitOrQueueOptions): Promise<SubmitOrQueueResult> {
  const { method, path, body, token, queueType, queuePayload } = options;
  if (!token) return { ok: false, queued: false };

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body != null ? JSON.stringify(body) : undefined,
    });

    if (response.ok) {
      return { ok: true, queued: false, status: response.status };
    }

    if (!isRetryable(response.status, null)) {
      return { ok: false, queued: false, status: response.status };
    }
  } catch (error) {
    if (!isRetryable(undefined, error)) {
      return { ok: false, queued: false };
    }
  }

  if (queueType && queuePayload) {
    await enqueueAction(queueType, queuePayload);
  } else {
    await enqueueAction('api_request', { method, path, body });
  }

  window.dispatchEvent(new CustomEvent(ACTION_QUEUE_CHANGED_EVENT));

  if (navigator.onLine) {
    void processActionQueue();
  }

  return { ok: true, queued: true };
}
